import type { ReactElement } from 'react';
import { useEffect, useMemo, useState } from 'react';
import type { CreateShowRequest, ModelShowPricing, ShowType, ShowTypeInfo } from '@fetmode/types';
import { t } from '@fetmode/i18n';
import { Button } from '@fetmode/ui';
import { MODEL_START_SHOW_TYPES, TICKET_DURATION_OPTIONS } from '@fetmode/utils';

import { useAuth } from '@fetmode/app-core';
import './ShowTypeStartPanel.css';

export interface ShowTypeStartPanelProps {
  isSubmitting: boolean;
  onStart: (type: ShowType, options?: Omit<CreateShowRequest, 'type'>) => Promise<void>;
}

function toLocalDateTimeValue(date: Date): string {
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${String(date.getFullYear())}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function ShowTypeStartPanel({
  isSubmitting,
  onStart,
}: ShowTypeStartPanelProps): ReactElement {
  const { api } = useAuth();
  const [types, setTypes] = useState<ShowTypeInfo[]>([]);
  const [pricing, setPricing] = useState<ModelShowPricing | null>(null);
  const [selectedType, setSelectedType] = useState<ShowType>('MAIN_STAGE');
  const [scheduledAt, setScheduledAt] = useState(() =>
    toLocalDateTimeValue(new Date(Date.now() + 60 * 60_000)),
  );
  const [durationMinutes, setDurationMinutes] = useState<number>(60);
  const [ticketCapacity, setTicketCapacity] = useState('');
  const [entryFeeCoins, setEntryFeeCoins] = useState('');
  const [minParticipants, setMinParticipants] = useState('2');
  const [coinsPerMinute, setCoinsPerMinute] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    void api.listShowTypes().then((result) => {
      setTypes(
        result.types.filter((type) =>
          MODEL_START_SHOW_TYPES.includes(type.type as (typeof MODEL_START_SHOW_TYPES)[number]),
        ),
      );
    });
    void api.getModelShowPricing().then((result) => {
      setPricing(result.pricing);
      setDurationMinutes(result.pricing.ticketDurationMinutes);
      setEntryFeeCoins(String(result.pricing.partyEntryCoins));
      setMinParticipants(String(result.pricing.partyMinParticipants));
      setCoinsPerMinute(
        String(
          Math.max(
            1,
            Math.floor(
              (result.pricing.vipCoinsPerMinute * result.pricing.spyDiscountPercent) / 100,
            ),
          ),
        ),
      );
      if (result.pricing.ticketCapacity !== null) {
        setTicketCapacity(String(result.pricing.ticketCapacity));
      }
    });
  }, [api]);

  useEffect(() => {
    if (!pricing) {
      return;
    }
    if (selectedType === 'PARTY') {
      setEntryFeeCoins(String(pricing.partyEntryCoins));
      setMinParticipants(String(pricing.partyMinParticipants));
    }
    if (selectedType === 'TICKET') {
      setEntryFeeCoins(String(pricing.ticketPriceCoins));
      setDurationMinutes(
        (TICKET_DURATION_OPTIONS as readonly number[]).includes(pricing.ticketDurationMinutes)
          ? pricing.ticketDurationMinutes
          : 60,
      );
      setTicketCapacity(
        pricing.ticketCapacity !== null ? String(pricing.ticketCapacity) : '',
      );
    }
    if (selectedType === 'PEEP') {
      setCoinsPerMinute(
        String(
          Math.max(
            1,
            Math.floor((pricing.vipCoinsPerMinute * pricing.spyDiscountPercent) / 100),
          ),
        ),
      );
    }
  }, [pricing, selectedType]);

  const canSubmit = useMemo(() => {
    if (selectedType === 'TICKET') {
      return scheduledAt.length > 0 && durationMinutes > 0;
    }
    if (selectedType === 'PARTY') {
      if (pricing?.partyEnabled === false) {
        return false;
      }
      const min = Number(minParticipants);
      const fee = Number(entryFeeCoins);
      return Number.isFinite(min) && min >= 2 && Number.isFinite(fee) && fee >= 1;
    }
    if (selectedType === 'PEEP') {
      const rate = Number(coinsPerMinute);
      return Number.isFinite(rate) && rate >= 1;
    }
    return true;
  }, [
    coinsPerMinute,
    durationMinutes,
    entryFeeCoins,
    minParticipants,
    pricing?.partyEnabled,
    scheduledAt,
    selectedType,
  ]);

  function buildOptions(): Omit<CreateShowRequest, 'type'> | undefined {
    if (selectedType === 'TICKET') {
      const capacityRaw = ticketCapacity.trim();
      const capacity =
        capacityRaw.length === 0 ? null : Math.max(1, Math.floor(Number(capacityRaw)));
      if (capacityRaw.length > 0 && !Number.isFinite(capacity)) {
        setFormError(t('common.studio.live.startParamsTicketCapacityInvalid'));
        return undefined;
      }
      const fee = Math.max(1, Math.floor(Number(entryFeeCoins)));
      if (!Number.isFinite(fee)) {
        setFormError(t('common.studio.live.startParamsTicketPriceInvalid'));
        return undefined;
      }
      return {
        scheduledAt: new Date(scheduledAt).toISOString(),
        durationMinutes,
        ticketCapacity: capacity,
        entryFeeCoins: fee,
      };
    }

    if (selectedType === 'PARTY') {
      const min = Math.max(2, Math.floor(Number(minParticipants)));
      const fee = Math.max(1, Math.floor(Number(entryFeeCoins)));
      if (!Number.isFinite(min) || !Number.isFinite(fee)) {
        setFormError(t('common.studio.live.startParamsPartyInvalid'));
        return undefined;
      }
      return {
        minParticipants: min,
        entryFeeCoins: fee,
      };
    }

    if (selectedType === 'PEEP') {
      const rate = Math.max(1, Math.floor(Number(coinsPerMinute)));
      if (!Number.isFinite(rate)) {
        setFormError(t('common.studio.live.startParamsPeepInvalid'));
        return undefined;
      }
      return { coinsPerMinute: rate };
    }

    return undefined;
  }

  return (
    <div className="show-type-start">
      <p className="show-type-start__hint">{t('common.studio.live.startShowHint')}</p>
      <fieldset className="show-type-start__fieldset">
        <legend className="show-type-start__legend">{t('common.studio.live.showTypeTitle')}</legend>
        <div
          className="show-type-start__grid"
          role="radiogroup"
          aria-label={t('common.studio.live.showTypeTitle')}
        >
          {types.map((type) => {
            const typeDisabled = type.type === 'PARTY' && pricing?.partyEnabled === false;
            const selected = selectedType === type.type;
            return (
              <label
                key={type.type}
                className={[
                  'fm-card',
                  'show-type-start__option',
                  selected ? 'show-type-start__option--selected' : '',
                  typeDisabled ? 'show-type-start__option--disabled' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <span className="show-type-start__option-row">
                  <input
                    type="radio"
                    className="show-type-start__radio"
                    name="showType"
                    checked={selected}
                    disabled={typeDisabled}
                    onChange={() => {
                      setSelectedType(type.type);
                      setFormError(null);
                    }}
                  />
                  <strong className="show-type-start__label">{type.label}</strong>
                </span>
                <p className="show-type-start__desc">
                  {typeDisabled ? t('common.studio.pricing.typeDisabled') : type.description}
                </p>
              </label>
            );
          })}
        </div>
      </fieldset>

      {selectedType === 'TICKET' ? (
        <div className="fm-card show-type-start__params">
          <p className="show-type-start__params-title">{t('common.studio.live.startParamsTicketTitle')}</p>
          <label className="show-type-start__field">
            {t('common.studio.live.scheduleShowAt')}
            <input
              className="fm-input"
              type="datetime-local"
              required
              value={scheduledAt}
              onChange={(event) => {
                setScheduledAt(event.target.value);
              }}
            />
            <span className="show-type-start__field-hint">{t('common.studio.live.scheduleShowHint')}</span>
          </label>
          <label className="show-type-start__field">
            {t('common.studio.live.startParamsDuration')}
            <select
              className="fm-input"
              value={String(durationMinutes)}
              onChange={(event) => {
                setDurationMinutes(Number(event.target.value));
              }}
            >
              {TICKET_DURATION_OPTIONS.map((minutes) => (
                <option key={minutes} value={minutes}>
                  {t('common.studio.live.startParamsDurationOption').replace(
                    '{{minutes}}',
                    String(minutes),
                  )}
                </option>
              ))}
            </select>
          </label>
          <label className="show-type-start__field">
            {t('common.studio.live.startParamsTicketPrice')}
            <input
              className="fm-input"
              type="number"
              min={1}
              value={entryFeeCoins}
              onChange={(event) => {
                setEntryFeeCoins(event.target.value);
              }}
            />
          </label>
          <label className="show-type-start__field">
            {t('common.studio.live.startParamsTicketCapacity')}
            <input
              className="fm-input"
              type="number"
              min={1}
              placeholder={t('common.studio.live.startParamsTicketCapacityUnlimited')}
              value={ticketCapacity}
              onChange={(event) => {
                setTicketCapacity(event.target.value);
              }}
            />
            <span className="show-type-start__field-hint">
              {t('common.studio.live.startParamsTicketCapacityHint')}
            </span>
          </label>
        </div>
      ) : null}

      {selectedType === 'PARTY' ? (
        <div className="fm-card show-type-start__params">
          <p className="show-type-start__params-title">{t('common.studio.live.startParamsPartyTitle')}</p>
          <label className="show-type-start__field">
            {t('common.studio.live.startParamsPartyEntry')}
            <input
              className="fm-input"
              type="number"
              min={1}
              value={entryFeeCoins}
              onChange={(event) => {
                setEntryFeeCoins(event.target.value);
              }}
            />
          </label>
          <label className="show-type-start__field">
            {t('common.studio.live.startParamsPartyMin')}
            <input
              className="fm-input"
              type="number"
              min={2}
              value={minParticipants}
              onChange={(event) => {
                setMinParticipants(event.target.value);
              }}
            />
          </label>
        </div>
      ) : null}

      {selectedType === 'PEEP' ? (
        <div className="fm-card show-type-start__params">
          <p className="show-type-start__params-title">{t('common.studio.live.startParamsPeepTitle')}</p>
          <label className="show-type-start__field">
            {t('common.studio.live.startParamsPeepRate')}
            <input
              className="fm-input"
              type="number"
              min={1}
              value={coinsPerMinute}
              onChange={(event) => {
                setCoinsPerMinute(event.target.value);
              }}
            />
          </label>
        </div>
      ) : null}

      {formError ? <p className="fm-error show-type-start__error">{formError}</p> : null}

      <div className="show-type-start__actions">
        <Button
          label={t('common.studio.live.startShow')}
          disabled={isSubmitting || !canSubmit}
          onClick={() => {
            setFormError(null);
            const options = buildOptions();
            if (selectedType === 'TICKET' || selectedType === 'PARTY' || selectedType === 'PEEP') {
              if (!options) {
                return;
              }
              void onStart(selectedType, options);
              return;
            }
            void onStart(selectedType);
          }}
        />
      </div>
    </div>
  );
}
